"use client"

/**
 * The chips a question row draws, in one place so the filter picker can draw
 * them too.
 *
 * Every one of these appears twice: in the cell, and beside a checkbox in the
 * column's filter popover. Two copies of "Hard is three red bars" is one copy
 * too many, and a picker that lists the words while the grid shows the bars
 * asks the reader to hold a key in their head.
 */

import { PillCell, SignalBarsCell } from "@/components/data-views"
import {
  BLOOM_ICON,
  BLOOM_UNTAGGED_ICON,
  DIFFICULTY_LEVEL,
  DIFFICULTY_TONE,
  type AssessmentBankQuestion,
  type BloomLevel,
} from "@/lib/mock/assessment-add-questions"

type Difficulty = AssessmentBankQuestion["difficulty"]

/** Easiest first, which is how a picker of three levels wants to read. */
export const DIFFICULTIES: readonly Difficulty[] = ["Easy", "Medium", "Hard"]

export function difficultyBars(difficulty: Difficulty) {
  return (
    <SignalBarsCell
      level={DIFFICULTY_LEVEL[difficulty]}
      tone={DIFFICULTY_TONE[difficulty]}
      label={difficulty}
    />
  )
}

/**
 * The same bars with the word beside them, for the filter picker.
 *
 * In a cell the column header says "Difficulty" and three bars are read as a
 * scale. In a popover there is no header over the rows, so bars alone ask the
 * reader to know which count means Hard before they can pick it.
 */
export function difficultyBarsLabelled(difficulty: Difficulty) {
  return (
    <span className="flex items-center gap-2">
      {difficultyBars(difficulty)}
      <span className="text-sm text-foreground">{difficulty}</span>
    </span>
  )
}

export function bloomPill(level: BloomLevel) {
  return <PillCell label={level} icon={BLOOM_ICON[level]} />
}

/** Untagged is a real state in this bank, not a missing value. */
export function bloomPillOrUntagged(level: BloomLevel | undefined) {
  if (!level) return <PillCell label="Untagged" icon={BLOOM_UNTAGGED_ICON} />
  return bloomPill(level)
}

export function questionTypePill(type: string) {
  return <PillCell label={type} icon="fa-list-check" />
}
